/**
 * Compatibility wrapper: next/navigation → TanStack Router
 *
 * Provides drop-in replacements for Next.js navigation hooks:
 * - useRouter() → wraps useNavigate() with push/replace/back/refresh
 * - useParams() → wraps TanStack useParams()
 * - usePathname() → wraps useLocation().pathname
 * - useSearchParams() → wraps useSearch() with URLSearchParams-like API
 * - redirect() → imperative navigate
 * - notFound() → throws to trigger 404
 *
 * Migration note: After stabilization, replace these with native TanStack Router APIs.
 */
import {
  useNavigate,
  useParams as useTanStackParams,
  useLocation,
  useSearch,
  useRouter as useTanStackRouter,
} from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

/**
 * Drop-in replacement for next/navigation useRouter()
 */
export function useRouter() {
  const navigate = useNavigate();
  const tanstackRouter = useTanStackRouter();

  return useMemo(
    () => ({
      push: (path: string) => navigate({ to: path }),
      replace: (path: string) => navigate({ to: path, replace: true }),
      back: () => window.history.back(),
      forward: () => window.history.forward(),
      refresh: () => tanstackRouter.invalidate(),
      prefetch: (_path: string) => {
        // TanStack Router handles prefetching automatically
      },
    }),
    [navigate, tanstackRouter],
  );
}

/**
 * Drop-in replacement for next/navigation useParams()
 * Returns a plain object of route params.
 */
export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useTanStackParams({ strict: false } as any) as T;
}

/**
 * Drop-in replacement for next/navigation usePathname()
 */
export function usePathname(): string {
  const location = useLocation();
  return location.pathname;
}

/**
 * Drop-in replacement for next/navigation useSearchParams()
 * Returns a URLSearchParams object (matches Next.js ReadonlyURLSearchParams API).
 */
export function useSearchParams(): URLSearchParams {
  return useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search],
  );
}

/**
 * Drop-in replacement for next/navigation redirect()
 * Note: In TanStack Router, redirects are handled differently.
 * This throws a redirect for use in loaders/beforeLoad.
 */
export function redirect(path: string): never {
  // For client-side usage, just navigate
  window.location.href = path;
  // This never actually returns but TypeScript needs it
  throw new Error(`Redirecting to ${path}`);
}

/**
 * Drop-in replacement for next/navigation notFound()
 */
export function notFound(): never {
  throw new Error('NEXT_NOT_FOUND');
}
